import * as R from 'Router/routes';
import { Link } from 'components/index';
import { QUERY_CARD_ADDED } from 'utils/constants';
import { useActiveCard, useCardPayments, useClient } from 'hooks/webapi';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BankCardIcon from 'assets/icons/bank-card-circle.svg?react';
import Button from 'components/Button/Button';
import ClockIcon from 'assets/icons/clock-filled.svg?react';
import Cookies from 'js-cookie';
import GirlBackground from 'assets/icons/popup-save-card-modal-girl.svg?react';
import Modal from 'components/Modal/Modal';
import React, { memo, useCallback, useEffect, useState } from 'react';
import styles from 'components/Modals/CardMissingModal/CardMissingModal.module.scss';
import useError from 'hooks/useError';
import usePaymentPreferences from 'hooks/webapi/usePaymentPreferences';

export const COOKIE_MODAL_HIDDEN_NAME = 'cardMissingModalHidden';

export const setSessionMissingModalHidden = () => {
  sessionStorage.setItem(COOKIE_MODAL_HIDDEN_NAME, 'true');
};

export default memo(function CardMissingModal() {
  const { search } = useLocation();
  const { t } = useTranslation('cardMissingModal');
  const { client } = useClient();
  const { activeCard, fetchingCard } = useActiveCard();
  const { fetchingPaymentPreferences, paymentPreferences, updateAutorepay } =
    usePaymentPreferences();
  const { showError } = useError();
  const { saveCreditCard } = useCardPayments();
  const urlParams = new URLSearchParams(decodeURIComponent(search));

  const isSessionCookieHidden = Boolean(
    Cookies.get(COOKIE_MODAL_HIDDEN_NAME) ||
      sessionStorage.getItem(COOKIE_MODAL_HIDDEN_NAME),
  );

  const [showModal, setShowModal] = useState(false);
  const [pending, setPending] = useState(false);

  const changeShowModal = (nextShowModalValue: boolean) => {
    if (nextShowModalValue !== showModal) setShowModal(nextShowModalValue);
  };

  useEffect(() => {
    if (isSessionCookieHidden) return;

    const isDataLoaded = !fetchingCard && !fetchingPaymentPreferences;
    const preferencesCondition =
      activeCard === null ||
      (activeCard && paymentPreferences?.autoRepay === false);

    const nextShowModal = Boolean(
      isDataLoaded &&
        client?.hasOpenLoan &&
        preferencesCondition &&
        urlParams.get(QUERY_CARD_ADDED) !== 'true',
    );

    if (showModal !== nextShowModal) {
      changeShowModal(nextShowModal);
    }
  }, [client, activeCard, fetchingCard, fetchingPaymentPreferences, paymentPreferences, showModal]);

  const onClose = () => {
    setSessionMissingModalHidden();
    changeShowModal(false);
  };

  const handleLinkNotToday = () => {
    const endToday = new Date(new Date().setUTCHours(23, 59, 59));

    Cookies.set(COOKIE_MODAL_HIDDEN_NAME, 'true', { expires: endToday });

    changeShowModal(false);
  };

  const savePaymentCard = useCallback(async () => {
    setPending(true);

    // Activate autorepay
    if (activeCard && paymentPreferences?.autoRepay === false) {
      try {
        await updateAutorepay(true);
      } catch (e) {
        showError(e);
      } finally {
        setPending(false);
        onClose();
      }
      return;
    }

    // Save credit card
    if (client) {
      const commonParams = {
        successUrl:
          import.meta.env.VITE_ROOT_URL +
          R.ACTIVE_LOAN +
          `?${QUERY_CARD_ADDED}=true`,
        failureUrl: import.meta.env.VITE_ROOT_URL + R.ACTIVE_LOAN,
      };
      try {
        const response = await saveCreditCard({
          body: commonParams,
        });

        if (response && response.status === 'OK' && response.payformUrl) {
          window.location.assign(response.payformUrl);
        }
      } catch (e) {
        showError(e);
      } finally {
        setPending(false);
      }
    }
  }, [activeCard, client, paymentPreferences, saveCreditCard]);

  return (
    <>
      {showModal && (
        <Modal
          className={styles['modal-card']}
          closeClassName={styles['modal-card-close']}
          contentClassName={styles.modal}
          dataTest="modal-cardMissing"
          onClose={onClose}
        >
          <div className={styles['background-wrapper']}>
            <GirlBackground data-test="background-image" />
            <BankCardIcon
              className={styles['bank-card-icon']}
              data-test="modal-cardMissing-bank-icon"
            />
          </div>
          <div className={styles.title} data-test="modal-cardMissing-title">
            {t('title')}
          </div>
          <Button
            pending={pending}
            onClick={savePaymentCard}
            className={styles['submit-button']}
            dataTest="modal-cardMissing-submit-btn"
          >
            {t('submitBtn')}
          </Button>
          <div className={styles['sub-card-wrapper']}>
            <ClockIcon width="16px" data-test="modal-cardMissing-clock-icon" />
            <span
              className={styles['sub-card-text']}
              data-test="modal-cardMissing-subcard"
            >
              {t('subCard')}
            </span>
          </div>
          <div className={styles['footer-wrapper']}>
            <Link
              dataTest="modal-cardMissing-link"
              disabled={pending}
              onClick={handleLinkNotToday}
              className={styles['footer-link']}
            >
              {t('footer')}
            </Link>
          </div>
        </Modal>
      )}
    </>
  );
});
